import * as Cesium from "cesium";

import { DRAG_POINT_PICK_TOLERANCE_PIXELS, ENTITY_PREFIX } from "./measurementConstants";
import type { MeasurementType } from "@/stores/measurementStore";

export interface DraggingMeasurementState {
  measurementId: string;
  measurementType: MeasurementType;
  draggedPointRole: "start" | "end";
  fixedPoint: Cesium.Cartesian3;
  currentStartPoint: Cesium.Cartesian3;
  currentEndPoint: Cesium.Cartesian3;
  heightReferenceScreenY: number | null;
}

export const getMeasurementHandleFromPickedObject = (
  pickedObject: unknown,
): { measurementId: string; pointRole: "start" | "end" } | null => {
  if (!pickedObject || typeof pickedObject !== "object") return null;

  const entity = (pickedObject as { id?: unknown }).id;
  const entityId =
    entity && typeof entity === "object" && "id" in entity
      ? (entity as { id?: unknown }).id
      : entity;

  if (typeof entityId !== "string") return null;
  if (
    !entityId.startsWith(`${ENTITY_PREFIX}-`) ||
    (!entityId.endsWith("-start") && !entityId.endsWith("-end"))
  ) {
    return null;
  }

  const suffix = entityId.endsWith("-start") ? "-start" : "-end";
  const pointRole = suffix === "-start" ? "start" : "end";
  const measurementId = entityId.slice(
    `${ENTITY_PREFIX}-`.length,
    entityId.length - suffix.length,
  );

  return measurementId ? { measurementId, pointRole } : null;
};

export const findDraggableMeasurementHandle = (
  viewer: Cesium.Viewer,
  screenPosition: Cesium.Cartesian2,
): { measurementId: string; pointRole: "start" | "end" } | null => {
  const directPick = getMeasurementHandleFromPickedObject(
    viewer.scene.pick(screenPosition),
  );
  if (directPick) return directPick;

  const pickedObjects = viewer.scene.drillPick(screenPosition, 10);
  const drilledPick = pickedObjects
    .map((pickedObject) => getMeasurementHandleFromPickedObject(pickedObject))
    .find((handle) => handle !== null);

  if (drilledPick) return drilledPick;

  const pickedWithinTolerance = viewer.scene.drillPick(
    screenPosition,
    10,
    DRAG_POINT_PICK_TOLERANCE_PIXELS,
    DRAG_POINT_PICK_TOLERANCE_PIXELS,
  );

  return (
    pickedWithinTolerance
      .map((pickedObject) => getMeasurementHandleFromPickedObject(pickedObject))
      .find((handle) => handle !== null) ?? null
  );
};

export const setCameraInputsEnabled = (
  viewer: Cesium.Viewer,
  isEnabled: boolean,
) => {
  const controller = viewer.scene.screenSpaceCameraController;
  controller.enableInputs = isEnabled;
  controller.enableTranslate = isEnabled;
  controller.enableZoom = isEnabled;
  controller.enableRotate = isEnabled;
  controller.enableTilt = isEnabled;
  controller.enableLook = isEnabled;
};
